// React and related imports
import React, { useContext, useMemo } from "react";

// Third-party library imports
import { Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { duotone, light } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useWindowWidth } from "@react-hook/window-size";

// Local component imports
import { CoversWrapper } from "components/ModelList/ModelListUI";
import ModelCover from "./ModelCover";

// Local utility/context imports
import { ConfigContext } from "contexts/ConfigContext";
import { usePackage } from "contexts/PackageContext";
import { checkGroupsForCompCards } from "utils/modelPlaylists";
import { groupModelsByGroupName } from "utils/modelsGroups";
import { responsiveGridImagesPerRows } from "utils/NwUtils";
import ShortlistSendDrawer from "components/Shortlist/ShortlistSendDrawer";


const ModelList = ({ onModelClick }) => {
    const { clientConfig } = useContext(ConfigContext);
    const {
        grid: { imagesPerRow, imageAspectRatio },
        compcardTemplates,
        exportOptions
    } = clientConfig;
    const { currentPack, packageCode, useShortlist, view, messageDrawerOpen } = usePackage();
    const { Favourites: favs, Models: models } = currentPack;

    const width = useWindowWidth();
    const isMobile = width < 640;

    const parsedModels = useMemo(() => {
        let shortlist = { Models: [], hasCompCard: false, hasAllCompCards: false, numberOfCompCardsAvailable: 0 };
        if (useShortlist) {
            shortlist.Models = models.filter((model) => favs.some((fav) => fav.ModelId === model.ModelID));
            shortlist = checkGroupsForCompCards(shortlist);
        }
        const modelsToShow =
            view === "Shortlist" ? models.filter((model) => favs.some((fav) => fav.ModelId === model.ModelID)) : models;
        const { grouped, notGrouped } = groupModelsByGroupName(modelsToShow);
        const groupedModels = grouped.map((group) => {
            return checkGroupsForCompCards(group);
        });
        const noGroupModels = checkGroupsForCompCards(notGrouped);
        return {
            shortlist,
            groups: [...groupedModels, noGroupModels],
        };
    }, [models, view, favs]);

    const getImagesPerRow = () => {
        const maxImagesPerRow = imagesPerRow || 6;
        return responsiveGridImagesPerRows(width, maxImagesPerRow);
    };

    const getImageAspectRatio = () => {
        if (imageAspectRatio) {
            return imageAspectRatio;
        }
        return "16/9";
    };

    const downloadCompCards = (modelIDs) => {
        //get the first compcard playlist ID for each model in modelIDs
        const selectedModelsWithPlaylists = models.filter(
            (model) => modelIDs.includes(model.ModelID) && model.Playlists.some((playlist) => playlist.CompCard)
        );
        //get ID of the first compcard playlist for each model
        const packagePlaylistIDs = selectedModelsWithPlaylists.map(
            (model) => model.Playlists.find((playlist) => playlist.CompCard).ID
        );
        const packagePlaylistIDsString = packagePlaylistIDs.join("-");

        const template = compcardTemplates[0];

        let url = "/api/packages/printplaylists?";
        url += "PackagePlaylistIDs=" + packagePlaylistIDsString;
        url += "&PackageCode=" + packageCode;
        url += "&Template=" + template.file;
        url += "&IsCustom=" + (template.isCustom ? "true" : "false");
        url += "&Orientation=" + template.orientation;
        url += "&FileName=compcards";
        window.open(url, "_blank");
    };



    return (
        <>

            <CoversWrapper
                imagesPerRow={getImagesPerRow()}
                imageAspectRatio={getImageAspectRatio()}
            >
                {parsedModels.groups.map((modelGroup, index) => {
                    return modelGroup.Models.length === 0 ? (
                        <div key={`model-group-${index}`} />
                    ) : (
                        <div className="models-group-container" key={`model-group-${index}`}>
                            {modelGroup.GroupName !== "no group" || index !== 0 ? (
                                <h2 className="models-group-name">{modelGroup.GroupName}</h2>
                            ) : parsedModels.groups.length > 1 ? (
                                <h2 className="models-group-name">no group</h2>
                            ) : isMobile && parsedModels.shortlist.Models.length > 0 ? (
                                <h2 className="models-group-name">&nbsp;</h2>
                            ) : (
                                <h2 className="models-group-name"></h2>
                            )}
                            {isMobile && modelGroup.hasCompCard && exportOptions.pdfCompcard && (
                                <div className="models-group-toolbar-icons">
                                    <div
                                        className="download-compcards"
                                        onClick={() => downloadCompCards(modelGroup.Models.map((m) => m.ModelID))}
                                    >
                                        <Tooltip
                                            placement="left"
                                            title={`${
                                                modelGroup.hasAllCompCards
                                                    ? "download compcards"
                                                    : " download available compcards (" +
                                                      modelGroup.numberOfCompCardsAvailable +
                                                      " of " +
                                                      modelGroup.Models.length +
                                                      ")"
                                            }`}
                                        >
                                            <span className="fa-layers fa-fw fa-lg">
                                                <FontAwesomeIcon icon={light("image-polaroid-user")} />
                                                <FontAwesomeIcon
                                                    className="arrow-down"
                                                    icon={duotone("circle-arrow-down")}
                                                    transform="shrink-8 down-5 right-6"
                                                />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                            )}
                            <div className={`models-container`}>
                                {modelGroup.Models.map((model) => {
                                    return (
                                        <ModelCover
                                            key={`${model.ID}`}
                                            model={model}
                                            onModelClick={onModelClick}
                                            isMobile={isMobile}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
                {messageDrawerOpen && (
                    <ShortlistSendDrawer
                        shortlistModels={parsedModels.shortlist.Models}
                    />
                )}
            </CoversWrapper>
        </>
    );
};

export default ModelList;
